import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import * as Sentry from '@sentry/nuxt'
import { getApiErrorMessage } from '~/utils/getApiErrorMessage'
import { translate } from '#layers/base/i18n/t'

export type AvatarStatus = 'active' | 'pending' | 'rejected' | 'removed_by_admin'

/** Region-based deposit-method config, delivered in the session so the client
 *  can resolve which methods to show before the modal opens. Enforcement stays
 *  server-side. */
export interface DepositMethodsConfig {
  default: string[]
  countries: Record<string, string[]>
  masterToggles: Record<string, boolean>
}

export interface User {
  id: string
  walletAddress: string | null
  email: string | null
  username?: string | null
  /**
   * Equipped character nickname (player_equipped.display_name) — the PRIMARY
   * label shown in the topbar. Served by /auth-session alongside the account
   * `username`. The `displayName` getter prefers this so the reconcile path
   * surfaces the nickname immediately, before the character store's inventory
   * load (mirrors useDisplayNickname and the optimistic topbar cache).
   */
  displayName?: string | null
  firstName?: string | null
  lastName?: string | null
  isNewUser?: boolean
  authMethod?: string | null
  depositWalletAddress?: string | null
  depositWalletSolanaAddress?: string | null
  depositWalletTronAddress?: string | null
  /** Collective TON hot wallet address (env-sourced — same for every user). */
  depositWalletTonAddress?: string | null
  /** Per-user TON memo code — required for inbound USDT attribution on TON. */
  tonMemoCode?: string | null
  phone?: string | null
  avatarUrl?: string | null
  avatarStatus?: AvatarStatus | null
  avatarUpdatedAt?: string | null
  /** Effective fiat-deposit eligibility (global euroxen.enabled AND per-user flag). */
  fiatDepositsEnabled?: boolean
  /** Effective bitolo (MXN rails) eligibility — global bitolo.enabled AND per-user flag. */
  bitoloDepositsEnabled?: boolean
  /** Effective Speed (Lightning + on-chain rails) eligibility — global speed.enabled AND per-user flag. */
  speedDepositsEnabled?: boolean
  /** Region-based deposit-method config (default + per-country lists + master toggles). */
  depositMethods?: DepositMethodsConfig
}

export const useUserStore = defineStore('user', () => {
  // State
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const authInitialized = ref(false)

  // Promise that resolves when auth is initialized
  let authInitResolve: (() => void) | null = null
  const authInitPromise = new Promise<void>((resolve) => {
    authInitResolve = resolve
  })

  // Getters
  const isAuthenticated = computed(() => user.value !== null)

  const displayName = computed(() => {
    // Prefer firstName + lastName
    if (user.value?.firstName || user.value?.lastName) {
      return [user.value.firstName, user.value.lastName].filter(Boolean).join(' ')
    }
    // Equipped character nickname (player_equipped.display_name) — the name the
    // user actually set and sees. Preferred over the account handle so the
    // /auth-session reconcile (which carries both fields) shows the nickname
    // immediately, before the character store loads playerEquipped. Without
    // this the account username flashes in the topbar between reconcile and
    // inventory load (useDisplayNickname falls back to this getter).
    if (user.value?.displayName) return user.value.displayName
    // Account-level username (e.g. "EmberPanther6450") — fallback when no
    // nickname is set, so wallet-only users still get a real label over the
    // raw email/wallet.
    if (user.value?.username) return user.value.username
    if (user.value?.email) return user.value.email.split('@')[0]
    if (user.value?.walletAddress) {
      const addr = user.value.walletAddress
      return `${addr.slice(0, 6)}...${addr.slice(-4)}`
    }
    return 'Guest'
  })

  const initials = computed((): string => {
    const firstName = user.value?.firstName
    const lastName = user.value?.lastName
    // Use first letters of firstName and lastName if available
    if (typeof firstName === 'string' && typeof lastName === 'string' && firstName.length > 0 && lastName.length > 0) {
      return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
    }
    if (typeof firstName === 'string' && firstName.length > 0) {
      return firstName.slice(0, 2).toUpperCase()
    }
    const name = displayName.value
    if (!name || name === 'Guest') return 'G'
    return name.slice(0, 2).toUpperCase()
  })

  const walletAddress = computed(() => user.value?.walletAddress)
  const hasEmail = computed(() => !!user.value?.email)
  const hasPhone = computed(() => !!user.value?.phone)
  const depositWalletAddress = computed(() => user.value?.depositWalletAddress ?? null)
  const depositWalletSolanaAddress = computed(() => user.value?.depositWalletSolanaAddress ?? null)
  const depositWalletTronAddress = computed(() => user.value?.depositWalletTronAddress ?? null)
  const depositWalletTonAddress = computed(() => user.value?.depositWalletTonAddress ?? null)
  const tonMemoCode = computed(() => user.value?.tonMemoCode ?? null)

  // Actions
  async function fetchUser(accessToken: string): Promise<void> {
    const config = useRuntimeConfig()
    isLoading.value = true
    error.value = null

    try {
      // Send X-Session-Id for fast auth (Redis-only, avoids heavy bundle)
      const sessionId = typeof window !== 'undefined'
        ? localStorage.getItem('aurumrun_session_id')
        : null
      const response = await $fetch<{
        authenticated: boolean
        session: {
          userId: string
          walletAddress: string | null
          depositWalletAddress: string | null
          depositWalletSolanaAddress: string | null
          depositWalletTronAddress: string | null
          depositWalletTonAddress: string | null
          tonMemoCode: string | null
          email: string | null
          username?: string | null
          avatarUrl?: string | null
          avatarStatus?: AvatarStatus | null
          avatarUpdatedAt?: string | null
          fiatDepositsEnabled?: boolean
          bitoloDepositsEnabled?: boolean
          speedDepositsEnabled?: boolean
          depositMethods?: DepositMethodsConfig
        }
      }>(`${config.public.apiBase}/auth-session`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(sessionId && { 'X-Session-Id': sessionId }),
        },
      })

      if (response.authenticated && response.session) {
        user.value = {
          id: response.session.userId,
          walletAddress: response.session.walletAddress,
          depositWalletAddress: response.session.depositWalletAddress,
          depositWalletSolanaAddress: response.session.depositWalletSolanaAddress,
          depositWalletTronAddress: response.session.depositWalletTronAddress,
          depositWalletTonAddress: response.session.depositWalletTonAddress,
          tonMemoCode: response.session.tonMemoCode,
          email: response.session.email || null,
          username: response.session.username ?? null,
          firstName: null,
          lastName: null,
          avatarUrl: response.session.avatarUrl ?? null,
          avatarStatus: response.session.avatarStatus ?? null,
          avatarUpdatedAt: response.session.avatarUpdatedAt ?? null,
          fiatDepositsEnabled: response.session.fiatDepositsEnabled ?? false,
          bitoloDepositsEnabled: response.session.bitoloDepositsEnabled ?? false,
          speedDepositsEnabled: response.session.speedDepositsEnabled ?? false,
          depositMethods: response.session.depositMethods,
        }
      }
    } catch (err) {
      console.error('[userStore] Failed to fetch user:', err)
      Sentry.captureException(err, { tags: { store: 'user', action: 'fetchUser' } })
      error.value = getApiErrorMessage(err, translate('errors.user.failedToFetch'))
      user.value = null
    } finally {
      isLoading.value = false
    }
  }

  async function logout(accessToken?: string): Promise<void> {
    const config = useRuntimeConfig()
    try {
      const sessionId = typeof window !== 'undefined'
        ? localStorage.getItem('aurumrun_session_id')
        : null
      await $fetch(`${config.public.apiBase}/auth-logout`, {
        method: 'POST',
        headers: {
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          ...(sessionId && { 'X-Session-Id': sessionId }),
        },
      })
    } catch (error) {
      console.warn('[userStore] Logout API call failed:', error)
      Sentry.captureMessage('Logout API call failed', { level: 'warning', tags: { store: 'user', action: 'logout' } })
    } finally {
      user.value = null
    }
  }

  function setUser(newUser: User): void {
    user.value = newUser
  }

  function clearUser(): void {
    user.value = null
    error.value = null
  }

  async function uploadAvatar(blob: Blob): Promise<void> {
    const config = useRuntimeConfig()
    const sessionId = typeof window !== 'undefined'
      ? localStorage.getItem('aurumrun_session_id')
      : null
    if (!sessionId) throw new Error('NOT_AUTHENTICATED')

    const fd = new FormData()
    fd.append('file', blob, 'avatar.jpg')

    const res = await $fetch<{
      success: boolean
      avatarUrl: string
      avatarStatus: AvatarStatus
      avatarUpdatedAt: string
    }>(`${config.public.apiBase}/user-avatar-upload`, {
      method: 'POST',
      headers: { 'X-Session-Id': sessionId },
      body: fd,
    })

    if (user.value) {
      user.value = {
        ...user.value,
        avatarUrl: res.avatarUrl,
        avatarStatus: res.avatarStatus,
        avatarUpdatedAt: res.avatarUpdatedAt,
      }
    }
  }

  async function deleteAvatar(): Promise<void> {
    const config = useRuntimeConfig()
    const sessionId = typeof window !== 'undefined'
      ? localStorage.getItem('aurumrun_session_id')
      : null
    if (!sessionId) throw new Error('NOT_AUTHENTICATED')

    await $fetch(`${config.public.apiBase}/user-avatar-delete`, {
      method: 'DELETE',
      headers: { 'X-Session-Id': sessionId },
    })

    if (user.value) {
      user.value = {
        ...user.value,
        avatarUrl: null,
        avatarStatus: null,
        avatarUpdatedAt: null,
      }
    }
  }

  function setAuthInitialized(): void {
    authInitialized.value = true
    if (authInitResolve) {
      authInitResolve()
      authInitResolve = null
    }
  }

  async function waitForAuth(): Promise<void> {
    if (authInitialized.value) return
    return authInitPromise
  }

  return {
    // State
    user,
    isLoading,
    error,
    authInitialized,
    // Getters
    isAuthenticated,
    displayName,
    initials,
    walletAddress,
    hasEmail,
    hasPhone,
    depositWalletAddress,
    depositWalletSolanaAddress,
    depositWalletTronAddress,
    depositWalletTonAddress,
    tonMemoCode,
    // Actions
    fetchUser,
    logout,
    setUser,
    clearUser,
    setAuthInitialized,
    waitForAuth,
    uploadAvatar,
    deleteAvatar,
  }
})
